import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BusyIndicatorComponent } from '../../shared/busy-indicator/busy-indicator.component';
import { TransformPipelineViewModel } from './transformPipelineViewModel';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config/config.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog.service';

@Component({
    selector: 'app-transform-pipeline-dialog',
    templateUrl: './transform-pipeline-dialog.component.html',
    styleUrls: ['./transform-pipeline-dialog.component.css'],
    standalone: false
})
export class TransformPipelineDialogComponent implements OnInit {
  message = 'Please wait...';
  viewModel: TransformPipelineViewModel;
  hasError = false;
  isCancelling = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private confirmDialogService: ConfirmDialogService,
    private dialogRef: MatDialogRef<BusyIndicatorComponent>) {
      this.message = data.message;
      this.viewModel = data.viewModel;
  }

  close(): void {
    this.dialogRef.close();
    this.snackBar.dismiss();
  }

  ngOnInit(): void {
    this.dialogRef.afterOpened().subscribe(result => {
      // nothing to do...
    });

    this.dialogRef.afterClosed().subscribe(result => {
      // nothing to do...
    });
  }

  runInBackground(): void {

    this.dialogRef.close();

    this.snackBar.open(this.hasError ? 'Failed while ' + this.message : this.message, 'Dismiss', {
          duration: 60000 * 60,
          panelClass: this.hasError ? 'snackbar-error' : null
        });
  }

  /**
   * The Architect, 2026-09-30: a Cancel button here must not be the reflex
   * action for a job that's just large, not broken -- check real progress
   * first (the same CPU+I/O evidence the automatic wedge-recovery watchdog
   * uses, see RowWorkerPool.get_progress_snapshot()) and warn before letting
   * the Analyst throw away work that's actually running fine. Only when it
   * looks unhealthy (or nothing measurable is running at all) does Cancel
   * proceed without friction.
   */
  async cancel(): Promise<void> {
    if (this.isCancelling) { return; }
    this.isCancelling = true;
    try {
      const status = await this.configService.getRowWorkerProgressStatus();
      if (status?.active && status.healthy === true) {
        const confirmed = await this.confirmDialogService
          .openConfirmDialog('This transform looks like it\'s actively running normally, not stuck. Cancel it anyway?')
          .afterClosed()
          .toPromise();
        if (!confirmed) { return; }
      }
      await this.configService.cancelRowWorkerTask();
      this.dialogRef.close();
      this.snackBar.open('Cancel requested.', 'Dismiss', { duration: 8000 });
    } catch {
      // Best-effort: if the status/cancel calls themselves fail, still let
      // the Analyst close out of a dialog that isn't updating rather than
      // trapping them here.
      this.dialogRef.close();
      this.snackBar.open('Could not confirm cancellation -- the run may still be stopping.', 'Dismiss', { duration: 8000 });
    } finally {
      this.isCancelling = false;
    }
  }
}
